import { Link } from 'react-router-dom';
import { useState, useEffect } from 'react';
import TopStrip from '@/components/TopStrip';
import Navbar from '@/components/Navbar';
import HeroCarousel from '@/components/HeroCarousel';
import Footer from '@/components/Footer';
import PanchangamWidget from '@/components/PanchangamWidget';
import api from '@/lib/api';
import { useT } from "@/contexts/LanguageContext";
import { Flame, BookOpen, Heart, ChevronRight, Camera, Newspaper, BedDouble, HandCoins, Tv, IndianRupee, Clock, Users, CalendarDays, Radio, Sun } from 'lucide-react';
import { BOOKINGS_PAUSED } from '@/lib/bookingStatus';
import { sortByPriority } from '@/lib/sevaSamagri';

export default function Home() {
  const { t, te, heading } = useT();
  const [news, setNews] = useState([]);
  const [reminders, setReminders] = useState([]);
  const [gallery, setGallery] = useState([]);
  const [sevas, setSevas] = useState([]);
  const [visitors, setVisitors] = useState(null);
  const [liveBlogPosts, setLiveBlogPosts] = useState([]);

  useEffect(() => {
    api.get('/news').then(r => setNews(r.data.slice(0, 5))).catch(() => {});
    // "Today / Tomorrow: Amavasya ..." lines built from the panchangam, shown first in the ticker.
    api.get('/panchangam/reminders').then(r => setReminders(Array.isArray(r.data) ? r.data : [])).catch(() => {});
    api.get('/gallery?media_type=PHOTO').then(r => setGallery(r.data.slice(0, 6))).catch(() => api.get('/gallery').then(r => setGallery(r.data.filter(g => !g.media_type || g.media_type === 'PHOTO').slice(0, 6))).catch(() => {}));
    api.get('/sevas').then(r => setSevas(sortByPriority(r.data).slice(0, 4))).catch(() => {});
    api.get('/visitor-stats').then(r => setVisitors(r.data)).catch(err => console.error('Failed to fetch visitor stats:', err));
    api.get('/live-blog').then(r => setLiveBlogPosts(r.data.slice(0, 3))).catch(() => {});
  }, []);

  const featureCards = [
    { icon: HandCoins, to: '/donations', title: 'e-Hundi', titleTe: 'ఈ-హుండి', desc: 'Online donations from across the globe for temple welfare', descTe: 'ఆలయ సంక్షేమం కొరకు ప్రపంచవ్యాప్తంగా ఆన్‌లైన్ విరాళాలు', cta: 'Donate Now', ctaTe: 'ఇప్పుడు విరాళం ఇవ్వండి', color: '#D4AF37' },
    { icon: BookOpen, to: '/paroksha-seva', title: 'Paroksha Seva', titleTe: 'పరోక్ష సేవ', desc: 'Participate in sevas without being physically present', descTe: 'నేరుగా హాజరు కాకుండానే సేవలలో పాల్గొనండి', cta: BOOKINGS_PAUSED ? 'More Info' : 'Book Now', ctaTe: BOOKINGS_PAUSED ? 'మరిన్ని వివరాలు' : 'ఇప్పుడు బుక్ చేయండి', color: '#621B00' },
    { icon: Heart, to: '/donations/annaprasadam', title: 'AnnaPrasadam', titleTe: 'అన్నప్రసాదం', desc: 'Sponsor sacred food offering for devotees', descTe: 'భక్తుల కొరకు పవిత్ర అన్నప్రసాద సేవను స్పాన్సర్ చేయండి', cta: 'Donate Now', ctaTe: 'ఇప్పుడు విరాళం ఇవ్వండి', color: '#C43E00' },
    { icon: BedDouble, to: '/accommodation', title: 'Accommodation', titleTe: 'వసతి', desc: 'Rooms, cottages and guest houses for visiting pilgrims', descTe: 'యాత్రికుల కొరకు గదులు, కాటేజీలు మరియు అతిథి గృహాలు', cta: BOOKINGS_PAUSED ? 'More Info' : 'Book Now', ctaTe: BOOKINGS_PAUSED ? 'మరిన్ని వివరాలు' : 'ఇప్పుడు బుక్ చేయండి', color: '#621B00' },
    { icon: Tv, to: '/media/live-tv', title: 'Temple TV', titleTe: 'ఆలయ టీవీ', desc: '24x7 live darshan and devotional programs', descTe: '24x7 ప్రత్యక్ష దర్శనం మరియు భక్తి కార్యక్రమాలు', cta: 'Watch Now', ctaTe: 'ఇప్పుడు చూడండి', color: '#C43E00' },
    { icon: Camera, to: '/gallery', title: 'Photo Gallery', titleTe: 'ఫోటో గ్యాలరీ', desc: 'Beautiful moments from the sacred temple', descTe: 'పవిత్ర ఆలయం నుండి అందమైన క్షణాలు', cta: 'View Gallery', ctaTe: 'గ్యాలరీ చూడండి', color: '#D4AF37' },
  ];

  const mainOfferings = [
    { name: "Abhishekam", nameTe: "అభిషేకం", desc: "Sacred bathing ritual with milk, water, honey", descTe: "పాలు, నీరు, తేనెతో పవిత్ర అభిషేకం" },
    { name: "Archana", nameTe: "అర్చన", desc: "Flower offering with sacred names chanting", descTe: "పవిత్ర నామాల జపంతో పుష్పార్చన" },
    { name: "Kumkuma Archana", nameTe: "కుంకుమ అర్చన", desc: "Special archana with kumkum powder", descTe: "కుంకుమ పొడితో ప్రత్యేక అర్చన" },
    { name: "Sahasranama", nameTe: "సహస్రనామ అర్చన", desc: "Chanting of 1000 divine names", descTe: "1000 దివ్య నామాల జపం" },
    { name: "Kalyanam", nameTe: "కల్యాణం", desc: "Celestial marriage ceremony", descTe: "దివ్య కల్యాణ మహోత్సవం" },
    { name: "Rudra Abhishekam", nameTe: "రుద్ర అభిషేకం", desc: "Grand abhishekam with Rudra chanting", descTe: "రుద్ర జపంతో మహా అభిషేకం" },
  ];

  return (
    <div className="min-h-screen bg-[#FFFCF5]">
      <TopStrip />
      <Navbar />

      {/* Hero Carousel */}
      <HeroCarousel />

      {/* News Ticker */}
      {(news.length > 0 || reminders.length > 0) && (
        <div className="bg-[#D4AF37]/10 border-b border-[#D4AF37]/20 overflow-hidden" data-testid="news-ticker">
          <div className="max-w-7xl mx-auto px-4 flex items-center gap-3 py-2.5">
            <span className="bg-[#C43E00] text-white text-xs px-3 py-1 rounded-full font-medium shrink-0 flex items-center gap-1">
              <Newspaper className="h-3 w-3" /> {t('News', 'వార్తలు')}
            </span>
            <div className="overflow-hidden whitespace-nowrap flex-1">
              <div className="inline-block animate-marquee">
                {reminders.map((r) => (
                  <Link key={r.id} to={`/panchangam?date=${r.date}`} className="inline-flex items-center gap-1 text-sm font-medium text-[#621B00] mx-8 hover:text-[#C43E00] transition-colors" data-testid="ticker-reminder">
                    <Sun className="h-3.5 w-3.5 text-[#D4AF37]" />
                    {t(r.title, r.title_telugu)}
                  </Link>
                ))}
                {news.map((n) => (
                  <Link key={n.id} to="/news" className="inline text-sm text-[#621B00] mx-8 hover:text-[#C43E00] transition-colors">
                    {n.is_important && <span className="text-[#C43E00] font-bold mr-1">*</span>}
                    {t(n.title, n.title_telugu)}
                  </Link>
                ))}
              </div>
            </div>
            <span className="hidden md:flex items-center gap-1.5 text-xs text-[#621B00]/70 border-l border-[#D4AF37]/30 pl-3 shrink-0" data-testid="ticker-date">
              <CalendarDays className="h-3.5 w-3.5 text-[#C43E00]" />
              {new Date().toLocaleDateString('en-IN', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' })}
            </span>
            {visitors && (
              <span className="hidden sm:flex items-center gap-1.5 text-xs text-[#621B00]/70 border-l border-[#D4AF37]/30 pl-3 shrink-0" data-testid="ticker-visitors">
                <Users className="h-3.5 w-3.5 text-[#C43E00]" />
                {t(`${visitors.total_visitors?.toLocaleString()} visited`, `${visitors.total_visitors?.toLocaleString()} సందర్శించారు`)}
              </span>
            )}
            <Link to="/news" className="text-xs text-[#C43E00] hover:underline shrink-0 font-medium border-l border-[#D4AF37]/30 pl-3" data-testid="view-all-news">{t('View All', 'అన్నీ చూడండి')}</Link>
          </div>
        </div>
      )}

      {/* Panchangam */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-10">
        <PanchangamWidget />
      </section>

      {/* Live Blog */}
      {liveBlogPosts.length > 0 && (
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-5">
          <div className="bg-white border border-[#E6DCCA] rounded-xl p-5" data-testid="live-blog-preview">
            <div className="flex items-center justify-between mb-3">
              <h3 className={`${heading} text-sm text-[#621B00] flex items-center gap-2`}>
                <Radio className="h-4 w-4 text-red-600" /> {t('Live Blog', 'లైవ్ బ్లాగ్')}
                <span className="w-1.5 h-1.5 bg-red-500 rounded-full animate-pulse" />
              </h3>
              <Link to="/live-blog" className="text-xs text-[#C43E00] hover:underline flex items-center gap-0.5">
                {t('View All', 'అన్నీ చూడండి')} <ChevronRight className="h-3 w-3" />
              </Link>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {liveBlogPosts.map(p => (
                <Link key={p.id} to="/live-blog" className="block hover:opacity-80 transition-opacity">
                  <p className="text-xs text-[#C43E00] font-medium uppercase tracking-wide">{t(p.event_name, p.event_name_telugu)}</p>
                  <p className="text-sm text-[#2D1B0E]">{t(p.title, p.title_telugu)}</p>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Key Feature Cards */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-16">
        <div className="text-center mb-10">
          <h2 className={`${heading} text-2xl md:text-3xl text-[#621B00] mb-1`}>{t("Temple Services", "దేవస్థానం సేవలు")}</h2>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {featureCards.map((c, i) => (
            <div key={i} className="bg-white border border-[#E6DCCA] rounded-xl p-6 hover:border-[#D4AF37]/50 hover:shadow-lg transition-all group" data-testid={`feature-card-${i}`}>
              <div className="flex items-start gap-4 mb-4">
                <div className="w-12 h-12 rounded-full flex items-center justify-center shrink-0" style={{backgroundColor: `${c.color}15`}}>
                  <c.icon className="h-6 w-6" style={{color: c.color}} />
                </div>
                <div className="flex-1">
                  <h3 className={`${heading} text-sm text-[#2D1B0E] tracking-wide group-hover:text-[#C43E00] transition-colors`}>{t(c.title, c.titleTe)}</h3>
                </div>
              </div>
              <p className="text-xs text-[#5D4037] mb-4 leading-relaxed">{t(c.desc, c.descTe)}</p>
              <Link to={c.to} className="inline-flex items-center gap-1 px-4 py-2 bg-[#C43E00] text-white text-xs rounded-full hover:bg-[#C43E00]/90 transition-all" data-testid={`feature-cta-${i}`}>
                {t(c.cta, c.ctaTe)} <ChevronRight className="h-3.5 w-3.5" />
              </Link>
            </div>
          ))}
        </div>
      </section>

      {/* UPDATED: About Section / Sthala Puranam */}
      <section className="bg-[#2D1B0E] text-white py-12 md:py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-10 items-center">
            <div>
              <h2 className={`${heading} text-2xl md:text-3xl mb-4`}>{t("Sthala Puranam: The 108th Parashurama Linga", "స్థలపురాణము: 108వ పరశురామ లింగం")}</h2>
              <p className="text-[#FFE0B2]/80 leading-relaxed mb-3 text-sm">
                {te ? (
                  <>త్రేతాయుగమున, శ్రీమహావిష్ణువు ఆరవ అవతారమైన <strong>శ్రీ పరశురాముడు</strong> భూమిని శుద్ధిచేసి సృష్టి సమతుల్యతను పునరుద్ధరించుటకు పవిత్ర యాత్ర చేపట్టెను. జమదగ్ని మహర్షి ఉపదేశము మేరకు, భారతదేశమంతట సరిగ్గా <strong>108 శివలింగములను</strong> ప్రతిష్ఠించెను.</>
                ) : (
                  <>In the sacred Treta Yuga, the sixth incarnation of Lord Vishnu, <strong>Lord Parashurama</strong>, undertook a divine pilgrimage to cleanse the earth and restore cosmic balance. Following the guidance of Sage Jamadagni, he consecrated exactly 108 Shiva Lingas across Bharata Varsha.</>
                )}
              </p>
              <p className="text-[#FFE0B2]/80 leading-relaxed mb-6 text-sm">
                {te ? (
                  <>ఆధ్యాత్మిక శక్తిగల <strong>ఇక్ష్వాద్రి (చెరువుగట్టు)</strong> గిరిని చేరుకొని, పరశురాముడు ఆ కొండపైగల గుహలో అరుదైన పశ్చిమాభిముఖముగా <strong>108వ మరియు చివరి శివలింగమును</strong> ప్రతిష్ఠించెను. తీవ్ర తపస్సు ఫలితంగా, పరమేశ్వరుడు ప్రకాశవంతమైన <em>జడలతో</em> ప్రత్యక్షమై అతనికి పాపవిమోచనము ప్రసాదించెను.</>
                ) : (
                  <>Arriving at the spiritually potent hill of <strong>Ikshwadri (Cheruvugattu)</strong>, Lord Parashurama installed the <strong>108th and final Shiva Linga</strong> within a cave in a rare west-facing direction. Through his intense penance, Lord Shiva manifested with radiant matted locks (<em>Jadala</em>) to grant him absolution.</>
                )}
              </p>
              <Link to="/about" className="inline-flex items-center gap-2 px-5 py-2 border border-[#D4AF37] text-[#D4AF37] rounded-full text-sm hover:bg-[#D4AF37]/10 transition-all" data-testid="read-more-about">
                {t('Read Full History', 'పూర్తి చరిత్ర చదవండి')} <ChevronRight className="h-4 w-4" />
              </Link>
            </div>
            
            {/* Raja Ravi Varma's Parashurama (public domain, pre-1906) alongside
                photographs of the kshetram he is said to have consecrated. */}
            <div className="grid grid-cols-2 gap-3">
              <figure className="col-span-2">
                <div className="aspect-[16/9] rounded-xl overflow-hidden bg-black/20">
                  <img src="/Assets/Ikshwadri_Hill_2.webp" alt="Ikshwadri hill, Cheruvugattu" className="w-full h-full object-cover hover:scale-105 transition-transform duration-500" loading="lazy" />
                </div>
                <figcaption className="mt-1.5 text-center text-xs text-[#FFE0B2]/50 italic">Ikshwadri</figcaption>
              </figure>
              <figure className="relative pt-2">
                <div className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1/2 w-2.5 h-2.5 rounded-full bg-[#D4AF37] shadow-[0_0_8px_2px_rgba(212,175,55,0.6)] z-10" aria-hidden="true" />
                <div className="absolute inset-x-0 top-0 aspect-[3/4] rounded-t-[999px] rounded-b-2xl bg-[radial-gradient(circle_at_50%_35%,rgba(212,175,55,0.35),transparent_70%)] blur-md" aria-hidden="true" />
                <div className="relative aspect-[3/4] rounded-t-[999px] rounded-b-2xl overflow-hidden border-2 border-[#D4AF37] shadow-[0_0_0_4px_#3D1F0A]">
                  <img src="/Assets/Sri_Swamy_Varu_Potrait_1.webp" alt="Sri Jadala Ramalingeshwara Swamy" className="w-full h-full object-cover hover:scale-105 transition-transform duration-500" loading="lazy" />
                </div>
                <figcaption className="mt-2 text-center text-xs text-[#FFE0B2]/50 italic">Sri Swamy Varu</figcaption>
              </figure>
              <figure className="relative pt-2">
                <div className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1/2 w-2.5 h-2.5 rounded-full bg-[#D4AF37] shadow-[0_0_8px_2px_rgba(212,175,55,0.6)] z-10" aria-hidden="true" />
                <div className="absolute inset-x-0 top-0 aspect-[3/4] rounded-t-[999px] rounded-b-2xl bg-[radial-gradient(circle_at_50%_35%,rgba(212,175,55,0.35),transparent_70%)] blur-md" aria-hidden="true" />
                <div className="relative aspect-[3/4] rounded-t-[999px] rounded-b-2xl overflow-hidden border-2 border-[#D4AF37] shadow-[0_0_0_4px_#3D1F0A]">
                  <img src="/Assets/Lord_Parashurama.webp" alt="Lord Parashurama, painted by Raja Ravi Varma" className="w-full h-full object-cover hover:scale-105 transition-transform duration-500" loading="lazy" />
                </div>
                <figcaption className="mt-2 text-center text-xs text-[#FFE0B2]/50 italic">Brahmarshi Parashurama</figcaption>
              </figure>
            </div>
          </div>
        </div>
      </section>

      {/* Pratyaksha Seva Preview */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-16">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h2 className={`${heading} text-2xl text-[#621B00] mb-1`}>{t("Pratyaksha Seva", "ప్రత్యక్ష సేవ")}</h2>
          </div>
          <Link to="/sevas" className="inline-flex items-center gap-1 text-sm text-[#C43E00] hover:underline" data-testid="explore-sevas-link">
            {t('Explore All', 'అన్నీ చూడండి')} <ChevronRight className="h-4 w-4" />
          </Link>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {sevas.map(seva => (
            <Link key={seva.id} to={`/book/${seva.id}`} className="bg-white border border-[#E6DCCA] rounded-xl p-5 hover:border-[#D4AF37]/50 hover:shadow-md transition-all group" data-testid={`seva-preview-${seva.id}`}>
              <h3 className={`${heading} text-sm text-[#2D1B0E] group-hover:text-[#C43E00] transition-colors`}>{t(seva.name_english, seva.name_telugu)}</h3>
              <div className="flex items-center justify-between mt-3">
                <span className="flex items-center gap-1 text-xs text-[#8D6E63]"><Clock className="h-3 w-3" /> {seva.duration_minutes} min</span>
                <span className="flex items-center gap-0.5 font-bold text-[#C43E00]"><IndianRupee className="h-3.5 w-3.5" />{seva.base_price}</span>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* Main Offerings */}
      <section className="bg-[#FDFBF7] border-y border-[#E6DCCA] py-12 md:py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between mb-8">
            <div>
              <h2 className={`${heading} text-2xl text-[#621B00] mb-1`}>{t("Main Offerings", "ప్రధాన సేవలు")}</h2>
            </div>
            <Link to="/sevas" className="inline-flex items-center gap-1 text-sm text-[#C43E00] hover:underline">{t('View All Offerings', 'అన్నీ సేవలు చూడండి')} <ChevronRight className="h-4 w-4" /></Link>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {mainOfferings.map((o, i) => (
              <div key={i} className="flex items-center gap-4 bg-white border border-[#E6DCCA] rounded-xl p-4 hover:border-[#D4AF37]/40 transition-all" data-testid={`offering-${i}`}>
                <div className="w-10 h-10 bg-[#C43E00]/10 rounded-full flex items-center justify-center shrink-0">
                  <Flame className="h-5 w-5 text-[#C43E00]" />
                </div>
                <div>
                  <h3 className="text-sm font-medium text-[#2D1B0E]">{t(o.name, o.nameTe)}</h3>
                  <p className="text-xs text-[#8D6E63] mt-0.5">{t(o.desc, o.descTe)}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Photo Gallery Preview */}
      {gallery.length > 0 && (
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-16">
          <div className="flex items-center justify-between mb-8">
            <div>
              <h2 className={`${heading} text-2xl text-[#621B00] mb-1`}>{t("The Beauty of Sacred Cheruvugattu", "పవిత్ర చెరువుగట్టు సౌందర్యం")}</h2>
            </div>
            <Link to="/gallery" className="inline-flex items-center gap-1 text-sm text-[#C43E00] hover:underline">{t('View All', 'అన్నీ చూడండి')} <ChevronRight className="h-4 w-4" /></Link>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
            {gallery.map((img, i) => (
              <Link to="/gallery" key={i} className="aspect-square rounded-xl overflow-hidden group">
                <img src={img.image_url} alt={img.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" loading="lazy" />
              </Link>
            ))}
          </div>
        </section>
      )}

      {/* Quick Links */}
      <section className="bg-[#FDFBF7] border-t border-[#E6DCCA] py-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            {[
              { to: '/print-ticket', label: 'Print Ticket', labelTe: 'టికెట్ ప్రింట్' },
              { to: '/booking/quick', label: 'Quick Booking', labelTe: 'త్వరిత బుకింగ్' },
              { to: '/my-bookings', label: 'My Bookings', labelTe: 'నా బుకింగ్‌లు' },
              { to: '/volunteer', label: 'Volunteer', labelTe: 'వాలంటీర్' },
              { to: '/support/how-to-reach', label: 'How to Reach', labelTe: 'ఎలా చేరుకోవాలి' },
            ].map((l, i) => (
              <Link key={i} to={l.to} className="bg-white border border-[#E6DCCA] rounded-xl p-4 text-center hover:border-[#D4AF37]/50 hover:shadow-md transition-all">
                <p className="text-sm font-medium text-[#2D1B0E]">{t(l.label, l.labelTe)}</p>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}